import type { ReactElement, ReactNode } from "react"
import { isValidElement } from "react"
import TextWithTooltipMol from "@onestore/hel/dist/components/molecules/TextWithTooltipMol"
import type { TooltipMolProps } from "@onestore/hel/dist/components/molecules/TooltipMol"
import TooltipMol from "@onestore/hel/dist/components/molecules/TooltipMol"
import MarkdownText from "@gatsby-plugin-generic-page/components/Markdown/Text"
import type { TooltipFlatData } from "@gatsby-plugin-generic-page/fragments/tooltip"
import UdsThemeProvider from "~/components/UdsThemeProvider"
import isEmpty from "~/lib/isEmpty"
import type { SquidexMarkdownEmbeddedContent } from "~/types/squidex"

type TooltipProps = Omit<TooltipFlatData, "text"> &
  Omit<TooltipMolProps, "children"> & {
    children: string | SquidexMarkdownEmbeddedContent | ReactNode
  }

function Tooltip({
  children,
  labelText = "",
  labelTypography,
  isUnderlineTooltipLabel = false,
  ...restProps
}: TooltipProps): ReactElement<TooltipProps> | null {
  const isMarkdownContent =
    typeof children === "string" ||
    (typeof children === "object" &&
      children !== null &&
      !isValidElement(children))

  const tooltipElementText = isMarkdownContent ? (
    <UdsThemeProvider>
      <MarkdownText typography={{ bold: "small3", default: "small2" }}>
        {children as string | SquidexMarkdownEmbeddedContent}
      </MarkdownText>
    </UdsThemeProvider>
  ) : (
    children
  )

  if (!isEmpty(labelText) && isUnderlineTooltipLabel) {
    return (
      <TextWithTooltipMol
        text={labelText}
        typography={labelTypography}
        tooltip={tooltipElementText}
      />
    )
  }

  return (
    <TooltipMol
      labelText={labelText}
      labelTypography={labelTypography}
      {...restProps}
    >
      {tooltipElementText}
    </TooltipMol>
  )
}

export default Tooltip
